import { useContext, useEffect, useState } from "react";
import { RxPlusCircled } from "react-icons/rx";
import { AccountContext } from "../../../contexts/AccountContexts";
import { loadingActive, pageLoadingActive } from "../../../components/Background";
import { CiEdit } from "react-icons/ci";
import { requestChangeActiveEmployeeAnnouncement, requestDeleteEmployeeAnnouncement, requestEditEmployeeAnnouncement, requestEmployeeAnnouncements, requestSubmitNewEmployeeAnnouncement } from "../../../requests/setup_requests";
import { ContentContext } from "../../../contexts/ContentContext";
import { PageHeader } from "../../../components/HeaderComponents";
import { useConfirm } from "../../../contexts/ModalContexts";
import { useTranslation } from "react-i18next";
import { PermissionContext } from "../../../contexts/PermissionContexts";
import { useNavigate } from "react-router-dom";
import { darkAndLightThemeDefault } from "../../../components/ThemeComponents";
import { AnnouncementsComponent } from "../../../components/AnnouncementsComponents";
import { LuInfinity } from "react-icons/lu";


export const EmployeeAnnouncementsSetup = () => {
    const { t } = useTranslation('settings')
    const { openErrorModal } = useContext(ContentContext)
    const { hasPermission } = useContext(PermissionContext)
    const [openNew, setOpenNew] = useState(false)
    const [announcements, setAnnouncements] = useState([]);
    const [editInProgress, setEditInProgress] = useState(false);

    const [inputKey, setInputKey] = useState(Date.now());

    const navigator = useNavigate()

    const handleOpenNew = () => {
        setOpenNew(true)
    }

    useEffect(() => {

        if (!hasPermission("announcement:view")) {
            navigator("/admin/dashboard")
            return
        }

        pageLoadingActive(true)
        requestEmployeeAnnouncements({ hasPermission : hasPermission("announcement:edit") })
            .then((data) => {
                setAnnouncements(data.announcements)
                pageLoadingActive(false)
            })
            .catch((error) => {
                console.error(error.status, error.message)
                openErrorModal(error)
                pageLoadingActive(false)
            })
    }, [])

    useEffect(() => {
        if (!editInProgress) {
            setInputKey(Date.now());
        }
    }, [editInProgress])

    useEffect(() => {
        if (openNew) {
            setEditInProgress(true);
        } else {
            setEditInProgress(false);
        }
    }, [openNew]);

    if (!hasPermission("announcement:view")) {
        return <></>
    }


    return (
        <>
            <div className="flex justify-center">
                <AnnouncementsComponent key={inputKey} fetchCall={requestEmployeeAnnouncements} />
            </div>
            <div className="flex flex-col w-full bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-600 rounded-md px-4 py-3 gap-3 min-h-[500px]">
                <PageHeader pageTitle={t("Employee Announcements Setup")} />
                <span>{t('Set up the employee announcements.')}</span>
                <div className="grid grid-cols-5 w-full gap-3 items-center">
                    <span className="label-text ml-2 text-base text-gray-800 dark:text-gray-300 font-semibold">
                        {t("Active")}
                    </span>
                    <span className="label-text text-base text-gray-800 dark:text-gray-300 font-semibold">
                        {t('Announcement')}
                    </span>
                    <span className="label-text text-base text-gray-800 dark:text-gray-300 font-semibold">
                        {t('Value')}
                    </span>
                    <span className="label-text text-base text-gray-800 dark:text-gray-300 font-semibold">
                        {t("Date")}
                    </span>
                </div>
                {
                    announcements.map((announcement) =>
                        <AnnouncementElement
                            key={announcement.id}
                            props={announcement}
                            setAnnouncements={setAnnouncements}
                            editInProgress={editInProgress}
                            setEditInProgress={setEditInProgress}
                        />
                    )
                }

                {
                    !openNew ?
                        (
                            hasPermission("announcement:create") &&
                            <div className="flex w-full justify-center mt-4">
                                <button className="btn w-full btn-md h-9 p-0 text-primary dark:text-blue-600 bg-white dark:bg-gray-700 hover:text-white hover:bg-primary  border-gray-500 dark:border-gray-500 hover:border-gray-300 dark:hover:border-gray-500"
                                    disabled={editInProgress}
                                    onClick={handleOpenNew}
                                >
                                    <RxPlusCircled size={30} />
                                </button>
                            </div>
                        )
                        :
                        <AnnouncementElementEdit
                            key={"$$$$$$$$$"}
                            props
                            setOpen={setOpenNew}
                            setElement={setAnnouncements}
                            edit={false}
                            placeholder="Announcement"
                        />
                }
            </div>
        </>
    )
}


const AnnouncementElement = ({ props, setAnnouncements, editInProgress, setEditInProgress }) => {
    const { hasPermission } = useContext(PermissionContext)

    const [edit, setEdit] = useState(false);
    const [newElements, setNewElements] = useState(null);

    const handleOpenEdit = () => {
        setEdit(true);
    };

    useEffect(() => {
        if (edit) {
            setEditInProgress(true);
        } else {
            setEditInProgress(false);
        }
    }, [edit]);

    useEffect(() => {
        if (newElements !== null) {
            setAnnouncements(newElements);
            setNewElements(null);
        }
    }, [newElements]);


    return !edit ? (
        <div className="flex flex-col w-full">

            <div className={`grid grid-cols-5 w-full gap-5 items-center group hover:bg-gray-100 dark:hover:bg-gray-600 border-gray-300 dark:border-gray-600 p-1 py-2 border rounded-lg ${editInProgress ? "bg-gray-100 dark:bg-gray-600" : ""}`}>
                <input
                  type="checkbox"
                  checked={props.active}
                  className="checkbox checkbox-success ml-2 hover:border-gray-500 bg-red-500 border-red-500"
                  onChange={(e) => {
                    requestChangeActiveEmployeeAnnouncement({
                      id: props.id,
                      active: e.target.checked,
                    })
                      .then((data) => {
                        setAnnouncements(data.announcements);
                      })
                  }}
                />
                <div className="flex flex-col gap-2">
                    <span className="font-semibold">{props.title}</span>
                    <span className="h-10 overflow-clip text-wrap whitespace-break-spaces hover:h-40 hover:overflow-auto max-h-max transition-all duration-200">{props.message}</span>
                </div>
                <span>{props.value}</span>
                <span className="w-max flex items-center">{props.startDate?.split("-").reverse().join(".")} - {props.endDate === "" ? <LuInfinity className="text-2xl ml-1" /> : props.endDate?.split("-").reverse().join(".")}</span>
                {
                    hasPermission("announcement:edit") &&
                    <button
                        className="btn btn-sm w-8 opacity-0 group-hover:opacity-100 transition-all duration-300 disabled:invisible ease-in-out border-none bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 hover:bg-primary dark:hover:bg-blue-600 hover:text-white p-0"
                        disabled={editInProgress}
                        onClick={handleOpenEdit}
                    >
                        <CiEdit size={24} />
                    </button>
                }
            </div>
        </div>
    ) : (
        <AnnouncementElementEdit
            key={props.id}
            props={props}
            setOpen={setEdit}
            setElement={setNewElements}
            edit={true}
            placeholder={"Announcement"}
        />
    );
}


const AnnouncementElementEdit = ({ props, setOpen, setElement, edit, placeholder }) => {
    const { t } = useTranslation('settings')
    const { user } = useContext(AccountContext);
    const { openErrorModal } = useContext(ContentContext)
    const getConfirmation = useConfirm()

    const [title, setTitle] = useState(props.title ? props.title : "");
    const [message, setMessage] = useState(props.message ? props.message : "");
    const [value, setValue] = useState(props.value ? props.value : 0);
    const [isPermanent, setIsPermanent] = useState(false);
    const [startDate, setStartDate] = useState(props.startDate ?? new Date().toISOString().split("T")[0]);
    const [endDate, setEndDate] = useState(props.endDate ?? null);

    const handleToggle = () => {
        setIsPermanent(!isPermanent);
        if(!isPermanent) {
            setEndDate("");
        } else if (!!isPermanent) {
            setEndDate(null);
        }
    };

    useEffect(() => {
        if (props.endDate === "") {
            setIsPermanent(true);
        }
    }, []);

    const handleSubmit = () => {
        loadingActive(true);
        if (!edit) {
            requestSubmitNewEmployeeAnnouncement({
                title: title,
                message: message,
                value: value,
                startDate: startDate,
                endDate: isPermanent ? "" : endDate
            }).then((data) => {
                setElement(data.announcements);
                setOpen(false);
                loadingActive(false);
            })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                    loadingActive(false)

                });
        } else {
            requestEditEmployeeAnnouncement({
                id: props.id,
                title: title,
                message: message,
                value: value,
                startDate: startDate,
                endDate: isPermanent ? "" : endDate
            }).then((data) => {
                setElement(data.announcements);
                setOpen(false);
                loadingActive(false);
            })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                    loadingActive(false)

                });
        }
    }

    const handleDelete = async () => {
        const isConfirmed = await getConfirmation(t("Are you sure you want to delete this item? This action cannot be undone."))
        if (isConfirmed) {
            loadingActive(true);
            requestDeleteEmployeeAnnouncement({ id: props.id }).then((data) => {
                setOpen((prev) => false);
                setElement(data.announcements);
                loadingActive(false);
            })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                    loadingActive(false)
                });
        }
    }


    const handleClose = () => {
        setOpen(false);
    }

    const submitCondition = 
        message === "" ||
        title == "" ||
        startDate === null ||
        endDate === null ||
        (title === props.title && 
            message === props.message && 
            value === props.value &&
            startDate === props.startDate &&
            endDate === props.endDate);

    return (
        <div className="grid grid-cols-3 w-full gap-3 items-center border p-1 py-2 rounded-lg">
            <div className="flex flex-col gap-8">
                <label className="form-control">
                    <input
                        type="text"
                        placeholder={t('Title')}
                        className="input input-bordered input-md w-full h-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300"
                        required={true}
                        value={title}
                        onChange={(e) => {
                            setTitle(e.target.value);
                        }}
                    />
                </label>
                <label className="form-control">
                    <textarea
                        placeholder={t('Message')}
                        className="textarea textarea-bordered textarea-md w-full h-40 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300"
                        required={true}
                        value={message}
                        onChange={(e) => {
                            setMessage(e.target.value);
                        }}
                    />
                </label>
            </div>
            <label className="form-control self-start space-y-2">
                <input
                    type="number"
                    placeholder={t('Value')}
                    data-theme="light"
                    className="input input-bordered  input-md w-full h-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300"
                    required={true}
                    value={value}
                    onChange={(e) => {
                        setValue(e.target.value);
                    }}
                />
                <label>{t("Start Date")}</label>
                <input
                    type="date"
                    className="input input-bordered focus:outline-none input-md w-full h-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                />
                <label>{t("End Date")}</label>
                <div className="flex items-center gap-2">
                    <input
                    type="checkbox"
                    id="permanentToggle"
                    checked={isPermanent}
                    onChange={handleToggle}
                    className="checkbox checkbox-success"
                    />
                    <label className="cursor-pointer" htmlFor="permanentToggle">{t("Indefinite")}</label>
                </div>
                <input
                    type="date"
                    className="input input-bordered focus:outline-none input-md w-full h-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300"
                    value={endDate || ""}
                    onChange={(e) => setEndDate(e.target.value)}
                    disabled={isPermanent}
                />
            </label>

            <div className="flex gap-3 mt-auto ltr:ml-auto rtl:mr-auto">
                <button
                    className="btn bg-green-600 hover:bg-green-600 border-none text-white"
                    onClick={handleSubmit}
                    disabled={submitCondition}
                >
                    {t('Submit')}
                </button>
                {edit && (
                    <button className="btn bg-red-600 hover:bg-red-600 border-none text-white" onClick={handleDelete}>
                        {t('Delete')}
                    </button>
                )}
                <button className={`btn ${darkAndLightThemeDefault}`} onClick={handleClose}>
                    {t('Cancel')}
                </button>
            </div>
        </div>
    )
}